import { useEffect, useState } from "react";
import { Check, X as XIcon, Edit3, Sparkles } from "lucide-react";

/** W076.3 day 5 — claims-projected facts panel.
 *
 *  Renders a single entity's typed facts straight from the claim
 *  ledger. Each fact shows inferred-vs-confirmed: solid border +
 *  check icon when confirmed, dashed border + sparkle when inferred.
 *  Confirm / Reject / Correct buttons hit the W076.3 routes; the
 *  panel refetches on action so the change is visible immediately.
 *
 *  Mounted inside PersonDossier, PlaceDossier, TripDetail as a
 *  narrow section. Does NOT replace the legacy domain reads; it
 *  surfaces the claims layer alongside them for now per the additive
 *  stage-1 rule. Day-8 surface overhaul will absorb / collapse the
 *  duplicates.
 */

export interface ProjectedFact {
  predicate: string;
  value: string;
  objectKind: string;
  objectId: string | null;
  objectJson: unknown;
  isConfirmed: boolean;
  isInferred: boolean;
  confidence: number;
  evidenceCount: number;
  sourcePacketIds: string[];
  winnerClaimId: string;
  lastObservedAt: string | null;
}

interface EntityProjection {
  subjectKind: string;
  subjectId: string;
  facts: Record<string, ProjectedFact>;
  collections: Record<string, ProjectedFact[]>;
  totalEvidence: number;
}

interface Props {
  kind: "person" | "place" | "trip" | "org" | "self";
  id: string;
  title?: string;
}

const PREDICATE_LABEL: Record<string, string> = {
  birthday_is: "Birthday",
  anniversary_is: "Anniversary",
  lives_at: "Lives at",
  works_at: "Works at",
  relationship_with: "Relationship",
  name_is: "Name",
  phone_is: "Phone",
  email_is: "Email",
  visited_place: "Visited",
  was_at: "Was at",
  near_to: "Near to",
  place_kind_is: "Kind of place",
  traveled_to: "Traveled to",
  stayed_at: "Stayed at",
  flew_on: "Flew on",
  departed_on: "Departed",
  returned_on: "Returned",
  met_with: "Met with",
  spoke_with: "Spoke with",
  committed_to: "Committed to",
  purchased: "Purchased",
  prefers: "Prefers",
  decided: "Decided",
};

function predicateLabel(p: string): string {
  return PREDICATE_LABEL[p] ?? p.replace(/_/g, " ");
}

async function postClaim(claimId: string, action: "confirm" | "reject" | "correct", body?: object): Promise<void> {
  const csrfMatch = document.cookie.match(/(?:^|; )memso_csrf=([^;]+)/);
  const csrf = csrfMatch ? decodeURIComponent(csrfMatch[1]) : "";
  await fetch(`/api/claims/${encodeURIComponent(claimId)}/${action}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(csrf ? { "X-CSRF-Token": csrf } : {}),
    },
    credentials: "same-origin",
    body: JSON.stringify(body ?? {}),
  });
}

export function ClaimedFactsPanel({ kind, id, title = "Memso knows" }: Props) {
  const [data, setData] = useState<EntityProjection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyClaim, setBusyClaim] = useState<string | null>(null);
  const [correctOpen, setCorrectOpen] = useState<string | null>(null);

  const load = () => {
    fetch(`/api/entities/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/claims`, {
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: EntityProjection) => setData(j))
      .catch((e: Error) => setError(e.message));
  };

  useEffect(() => {
    load();
  }, [kind, id]);

  const onAction = async (claimId: string, action: "confirm" | "reject", body?: object) => {
    setBusyClaim(claimId);
    try {
      await postClaim(claimId, action, body);
      load();
    } finally {
      setBusyClaim(null);
    }
  };

  const onCorrect = async (claimId: string, newValue: string) => {
    setBusyClaim(claimId);
    try {
      await postClaim(claimId, "correct", { corrected_value: newValue });
      load();
      setCorrectOpen(null);
    } finally {
      setBusyClaim(null);
    }
  };

  if (error) {
    return (
      <section className="rounded-2xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-900 dark:text-amber-100">
        Could not load claim layer: {error}
      </section>
    );
  }
  if (!data) {
    return (
      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/40 dark:bg-slate-800/30 p-4 text-xs text-slate-400">
        Loading facts…
      </section>
    );
  }

  const facts = Object.values(data.facts);
  const collections = Object.entries(data.collections);
  const isEmpty = facts.length === 0 && collections.length === 0;

  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5 space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
        <Sparkles className="w-4 h-4 text-violet-500" />
        {title}
        <span className="ml-auto text-[11px] text-slate-400 font-normal">
          {data.totalEvidence} claim{data.totalEvidence === 1 ? "" : "s"}
        </span>
      </h2>
      {isEmpty ? (
        <p className="text-xs text-slate-400 italic">
          No claim-backed facts yet. Memso will populate this as extractors run.
        </p>
      ) : (
        <>
          {facts.length > 0 && (
            <ul className="space-y-2">
              {facts.map((f) => (
                <FactRow
                  key={f.winnerClaimId}
                  fact={f}
                  busy={busyClaim === f.winnerClaimId}
                  onConfirm={() => onAction(f.winnerClaimId, "confirm")}
                  onReject={() => onAction(f.winnerClaimId, "reject")}
                  onCorrect={() => setCorrectOpen(f.winnerClaimId)}
                  correctOpen={correctOpen === f.winnerClaimId}
                  onSubmitCorrect={(v) => onCorrect(f.winnerClaimId, v)}
                  onCancelCorrect={() => setCorrectOpen(null)}
                />
              ))}
            </ul>
          )}
          {collections.map(([predicate, items]) => (
            <div key={predicate} className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1">
                {predicateLabel(predicate)} ({items.length})
              </div>
              <ul className="space-y-1.5">
                {items.slice(0, 12).map((f) => (
                  <FactRow
                    key={f.winnerClaimId}
                    fact={f}
                    busy={busyClaim === f.winnerClaimId}
                    onConfirm={() => onAction(f.winnerClaimId, "confirm")}
                    onReject={() => onAction(f.winnerClaimId, "reject")}
                    onCorrect={() => setCorrectOpen(f.winnerClaimId)}
                    correctOpen={correctOpen === f.winnerClaimId}
                    onSubmitCorrect={(v) => onCorrect(f.winnerClaimId, v)}
                    onCancelCorrect={() => setCorrectOpen(null)}
                    compact
                  />
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </section>
  );
}

/** Trip-specific projection panel. Trips are emitted as self-anchored
 *  claims with object_json.trip_id, so we query the dedicated
 *  /trips/:id/dossier/claims endpoint that filters by trip_id rather
 *  than the generic /entities path (which would look for
 *  subject_kind='trip' rows, of which there are none today). */
interface TripDossierClaims {
  trip_id: string;
  destinations: ProjectedFact[];
  cities: ProjectedFact[];
  hotels: ProjectedFact[];
  flights: ProjectedFact[];
  date_range: { start: string | null; end: string | null };
  totalEvidence: number;
}

export function TripClaimedFactsPanel({ tripId, title = "Memso knows" }: { tripId: string; title?: string }) {
  const [data, setData] = useState<TripDossierClaims | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyClaim, setBusyClaim] = useState<string | null>(null);
  const [correctOpen, setCorrectOpen] = useState<string | null>(null);

  const load = () => {
    fetch(`/api/trips/${encodeURIComponent(tripId)}/dossier/claims`, {
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: TripDossierClaims) => setData(j))
      .catch((e: Error) => setError(e.message));
  };

  useEffect(() => {
    load();
  }, [tripId]);

  const onAction = async (claimId: string, action: "confirm" | "reject", body?: object) => {
    setBusyClaim(claimId);
    try {
      await postClaim(claimId, action, body);
      load();
    } finally {
      setBusyClaim(null);
    }
  };
  const onCorrect = async (claimId: string, newValue: string) => {
    setBusyClaim(claimId);
    try {
      await postClaim(claimId, "correct", { corrected_value: newValue });
      load();
      setCorrectOpen(null);
    } finally {
      setBusyClaim(null);
    }
  };

  if (error) {
    return (
      <section className="rounded-2xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-900 dark:text-amber-100">
        Could not load claim layer: {error}
      </section>
    );
  }
  if (!data) {
    return (
      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/40 dark:bg-slate-800/30 p-4 text-xs text-slate-400">
        Loading claim layer…
      </section>
    );
  }

  const groups: Array<{ label: string; items: ProjectedFact[] }> = [
    { label: "Destinations", items: data.destinations },
    { label: "Cities visited", items: data.cities },
    { label: "Stayed at", items: data.hotels },
    { label: "Flew on", items: data.flights },
  ].filter((g) => g.items.length > 0);

  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5 space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
        <Sparkles className="w-4 h-4 text-violet-500" />
        {title}
        <span className="ml-auto text-[11px] text-slate-400 font-normal">
          {data.totalEvidence} claim{data.totalEvidence === 1 ? "" : "s"}
        </span>
      </h2>
      {groups.length === 0 ? (
        <p className="text-xs text-slate-400 italic">
          No claim-backed facts for this trip yet.
        </p>
      ) : (
        groups.map((g) => (
          <div key={g.label}>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1">
              {g.label} ({g.items.length})
            </div>
            <ul className="space-y-1.5">
              {g.items.slice(0, 12).map((f) => (
                <FactRow
                  key={f.winnerClaimId}
                  fact={f}
                  busy={busyClaim === f.winnerClaimId}
                  onConfirm={() => onAction(f.winnerClaimId, "confirm")}
                  onReject={() => onAction(f.winnerClaimId, "reject")}
                  onCorrect={() => setCorrectOpen(f.winnerClaimId)}
                  correctOpen={correctOpen === f.winnerClaimId}
                  onSubmitCorrect={(v) => onCorrect(f.winnerClaimId, v)}
                  onCancelCorrect={() => setCorrectOpen(null)}
                  compact
                />
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}

function FactRow({
  fact,
  busy,
  onConfirm,
  onReject,
  onCorrect,
  correctOpen,
  onSubmitCorrect,
  onCancelCorrect,
  compact = false,
}: {
  fact: ProjectedFact;
  busy: boolean;
  onConfirm: () => void;
  onReject: () => void;
  onCorrect: () => void;
  correctOpen: boolean;
  onSubmitCorrect: (v: string) => void;
  onCancelCorrect: () => void;
  compact?: boolean;
}) {
  const [draft, setDraft] = useState(fact.value);
  const styleConfirmed = "border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/15";
  const styleInferred = "border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10";
  return (
    <li
      className={`flex flex-wrap items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs ${
        fact.isConfirmed ? styleConfirmed : styleInferred
      }`}
    >
      {!compact && (
        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
          {predicateLabel(fact.predicate)}
        </span>
      )}
      <span className="text-slate-800 dark:text-slate-100 font-medium">{fact.value}</span>
      <span className="text-[10px] text-slate-400">
        {fact.evidenceCount === 1
          ? "1 source"
          : `${fact.evidenceCount} sources`}
        {fact.confidence > 0 ? `, conf ${fact.confidence.toFixed(2)}` : ""}
        {fact.isConfirmed ? "" : " — inferred"}
      </span>
      <div className="ml-auto flex items-center gap-1">
        {!fact.isConfirmed && (
          <button
            onClick={onConfirm}
            disabled={busy}
            className="text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-800/40 rounded p-1 disabled:opacity-50"
            title="Confirm"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={onReject}
          disabled={busy}
          className="text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-800/40 rounded p-1 disabled:opacity-50"
          title="Reject"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onCorrect}
          disabled={busy}
          className="text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-800/40 rounded p-1 disabled:opacity-50"
          title="Correct"
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
      </div>
      {correctOpen && (
        <div className="basis-full flex items-center gap-2 pt-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1"
            placeholder="Corrected value"
          />
          <button
            onClick={() => onSubmitCorrect(draft.trim())}
            disabled={!draft.trim() || draft.trim() === fact.value}
            className="text-xs rounded bg-sky-600 hover:bg-sky-700 text-white px-2 py-1 disabled:opacity-50"
          >
            Save
          </button>
          <button
            onClick={onCancelCorrect}
            className="text-xs rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
          >
            Cancel
          </button>
        </div>
      )}
    </li>
  );
}
